import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getEstatisticasDashboard,
  getClientesNaoAtendidos,
  getClientesAtendidos,
  getEstatisticasEquipamentos,
} from '../services/dashboardService';

const DASHBOARD_CACHE_KEY = 'tecnozap_dashboard_cache_v1';
const DASHBOARD_CACHE_TTL_MS = 2 * 60 * 1000;

const readDashboardCache = () => {
  try {
    const raw = localStorage.getItem(DASHBOARD_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || !parsed?.data) return null;
    const age = Date.now() - parsed.ts;
    if (age > DASHBOARD_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch (err) {
    return null;
  }
};

const writeDashboardCache = (data) => {
  try {
    localStorage.setItem(
      DASHBOARD_CACHE_KEY,
      JSON.stringify({ ts: Date.now(), data })
    );
  } catch (err) {
    // ignore cache write errors
  }
};

export const useDashboardData = () => {
  const cacheInicial = readDashboardCache();
  const [estatisticas, setEstatisticas] = useState(cacheInicial?.estatisticas ?? null);
  const [clientesNaoAtendidos, setClientesNaoAtendidos] = useState(
    cacheInicial?.clientesNaoAtendidos ?? []
  );
  const [clientesAtendidos, setClientesAtendidos] = useState(
    cacheInicial?.clientesAtendidos ?? []
  );
  const [estatisticasEquipamentos, setEstatisticasEquipamentos] = useState(
    cacheInicial?.estatisticasEquipamentos ?? []
  );
  const [loadingEstatisticas, setLoadingEstatisticas] = useState(true);
  const [loadingClientesNaoAtendidos, setLoadingClientesNaoAtendidos] =
    useState(true);
  const [loadingClientesAtendidos, setLoadingClientesAtendidos] = useState(true);
  const [loadingEstatisticasEquipamentos, setLoadingEstatisticasEquipamentos] =
    useState(true);
  const [errorEstatisticas, setErrorEstatisticas] = useState(null);
  const [errorClientesNaoAtendidos, setErrorClientesNaoAtendidos] = useState(null);
  const [errorClientesAtendidos, setErrorClientesAtendidos] = useState(null);
  const [errorEstatisticasEquipamentos, setErrorEstatisticasEquipamentos] =
    useState(null);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isMounted.current) return;
    writeDashboardCache({
      estatisticas,
      clientesNaoAtendidos,
      clientesAtendidos,
      estatisticasEquipamentos,
    });
  }, [
    estatisticas,
    clientesNaoAtendidos,
    clientesAtendidos,
    estatisticasEquipamentos,
  ]);

  const carregarEstatisticas = useCallback(async () => {
    setLoadingEstatisticas(true);
    setErrorEstatisticas(null);
    try {
      const data = await getEstatisticasDashboard();
      if (!isMounted.current) return;
      const payload = data?.data ?? data;
      setEstatisticas(payload);
    } catch (error) {
      if (!isMounted.current) return;
      setErrorEstatisticas(error?.message ?? 'Erro ao carregar estatísticas');
    } finally {
      if (isMounted.current) setLoadingEstatisticas(false);
    }
  }, []);

  const carregarClientesNaoAtendidos = useCallback(async () => {
    setLoadingClientesNaoAtendidos(true);
    setErrorClientesNaoAtendidos(null);
    try {
      const data = await getClientesNaoAtendidos();
      if (!isMounted.current) return;
      const payload = data?.data ?? data;
      setClientesNaoAtendidos(payload || []);
    } catch (error) {
      if (!isMounted.current) return;
      setErrorClientesNaoAtendidos(
        error?.message ?? 'Erro ao carregar não atendidos'
      );
    } finally {
      if (isMounted.current) setLoadingClientesNaoAtendidos(false);
    }
  }, []);

  const carregarClientesAtendidos = useCallback(async () => {
    setLoadingClientesAtendidos(true);
    setErrorClientesAtendidos(null);
    try {
      const data = await getClientesAtendidos();
      if (!isMounted.current) return;
      const payload = data?.data ?? data;
      setClientesAtendidos(payload || []);
    } catch (error) {
      if (!isMounted.current) return;
      setErrorClientesAtendidos(error?.message ?? 'Erro ao carregar atendidos');
    } finally {
      if (isMounted.current) setLoadingClientesAtendidos(false);
    }
  }, []);

  const carregarEstatisticasEquipamentos = useCallback(async () => {
    setLoadingEstatisticasEquipamentos(true);
    setErrorEstatisticasEquipamentos(null);
    try {
      const data = await getEstatisticasEquipamentos();
      if (!isMounted.current) return;
      const payload = data?.data ?? data;
      setEstatisticasEquipamentos(payload || []);
    } catch (error) {
      if (!isMounted.current) return;
      setErrorEstatisticasEquipamentos(
        error?.message ?? 'Erro ao carregar estatísticas de equipamentos'
      );
    } finally {
      if (isMounted.current) setLoadingEstatisticasEquipamentos(false);
    }
  }, []);

  const carregarTodosDados = useCallback(async () => {
    await Promise.all([
      carregarEstatisticas(),
      carregarClientesNaoAtendidos(),
      carregarClientesAtendidos(),
      carregarEstatisticasEquipamentos(),
    ]);
  }, [
    carregarEstatisticas,
    carregarClientesNaoAtendidos,
    carregarClientesAtendidos,
    carregarEstatisticasEquipamentos,
  ]);

  const recarregarDados = useCallback(() => {
    carregarTodosDados();
  }, [carregarTodosDados]);

  useEffect(() => {
    carregarTodosDados();
  }, [carregarTodosDados]);

  const isLoading =
    loadingEstatisticas ||
    loadingClientesNaoAtendidos ||
    loadingClientesAtendidos ||
    loadingEstatisticasEquipamentos;

  const hasError =
    errorEstatisticas ||
    errorClientesNaoAtendidos ||
    errorClientesAtendidos ||
    errorEstatisticasEquipamentos;

  return {
    estatisticas,
    clientesNaoAtendidos,
    clientesAtendidos,
    estatisticasEquipamentos,
    isLoading,
    hasError,
    recarregarDados,
  };
};
